import { IconButton } from "$components/button/icon-button";
import { ErrorState } from "$components/error-state";
import { SearchField } from "$components/search-field";
import { Table, type TableColumnDefinition } from "$components/table";
import { featureFlags } from "$global-providers/feature-flags-provider/feature-flags";
import { useFeatureFlag } from "$global-providers/feature-flags-provider/provider";
import { useUser } from "$global-providers/user-provider";
import ExpiryCell from "$routes/inventory/_lib/expiry-cell";
import { LocationSelectWithAllOption } from "$routes/inventory/_lib/location-select-with-all-option";
import { hasUpdateLotPermissions } from "$routes/inventory/_lib/permissions";
import { ProductOriginBadge } from "$routes/inventory/_lib/product-origin-badge";
import { ProductOriginFilter } from "$routes/inventory/_lib/product-origin-filter";
import { SortBySelect } from "$routes/inventory/_lib/sort-by-select";
import { Box, Grid, makeStyles } from "@material-ui/core";
import { Edit } from "@material-ui/icons";
import { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { EditLotModal } from "./edit-lot-modal/edit-lot-modal";
import { useInventoryTableFilterContext } from "./inventory-context";
import type { Lot } from "./queries";
import { useSearchLots } from "./use-search-lots";
import { useSortByOptions } from "./use-sort-by-options";
import { useUpdateLot } from "./use-update-lot";

const PAGE_SIZE = 20;

const styles = makeStyles((theme) => ({
  headerControls: {
    marginBottom: theme.spacing(3),
  },
  inheritFontWeight: {
    fontWeight: "inherit",
  },
  reducePadding: {
    padding: "0 10px",
  },
}));

function getTableColumns({
  handleEditClick,
  formatMessage,
  roleName,
}): TableColumnDefinition[] {
  const fields: TableColumnDefinition[] = [
    {
      field: "name",
      headerName: formatMessage({
        id: "inventory.overview.table.columnLabel.name",
        defaultMessage: "MEDICATION",
      }),
      flex: 8,
      renderCell: (value: string, _index, _column, row) => (
        <Box display="flex" alignItems="center" gridGap={8}>
          <span>{value}</span>
          <ProductOriginBadge
            externalReferences={row?.sku?.externalReferences}
          />
        </Box>
      ),
    },
    {
      field: "location",
      headerName: "",
      flex: 2,
    },
    {
      field: "quantity",
      headerName: formatMessage({
        id: "inventory.overview.table.columnLabel.quantity",
        defaultMessage: "UNITS",
      }),
      flex: 1,
      alignContents: "right",
    },
    {
      field: "expiryDate",
      headerName: formatMessage({
        id: "inventory.overview.table.columnLabel.expiryDate",
        defaultMessage: "EXPIRY DATE",
      }),
      width: 125,
      alignContents: "right",
      renderCell: (value: string) => <ExpiryCell expiryDate={value} />,
    },

    hasUpdateLotPermissions(roleName) && {
      field: "actions",
      width: 55,
      padding: "0 10px",
      alignContents: "right",
      hideContentsforGroupHeader: true,
      renderCell: (_, _index, _column, row) => (
        <IconButton
          aria-label="edit"
          aria-controls="edit"
          onClick={() => {
            handleEditClick(row);
          }}
        >
          <Edit />
        </IconButton>
      ),
    },
  ];

  return fields.filter(Boolean);
}

export const InventoryOverview = ({
  pageSize = PAGE_SIZE,
}: {
  pageSize?: number;
}): JSX.Element => {
  const classes = styles();
  const inventoryTableFilterState = useInventoryTableFilterContext();
  const { formatMessage } = useIntl();

  const { facilityId, role: roleName } = useUser();
  const [selectedLot, setSelectedLot] = useState<Lot | null>(null);
  const [openModal, setOpenModal] = useState<boolean>(false);
  const sortByOptions = useSortByOptions();
  const [currentPage, setCurrentPage] = useState(0);

  const {
    loading,
    locationId,
    setLocationId,
    sortBy,
    setSortBy,
    searchTerm,
    setSearchTerm,
    itemType,
    setItemType,
  } = inventoryTableFilterState;
  const [nonPharmaFlag] = useFeatureFlag(featureFlags.clinical.nonPharma);

  // Clear any persisted Pharma/Non-pharma filter when the flag flips off, so a
  // user who toggles between flag states doesn't end up with an invisible
  // server-side filter narrowing their results.
  useEffect(() => {
    if (!nonPharmaFlag && itemType !== null) {
      setItemType(null);
    }
  }, [nonPharmaFlag, itemType, setItemType]);

  function handleEditClick(lot: Lot) {
    setSelectedLot(lot);
    setOpenModal(true);
  }

  const handleLotEditSubmit = useUpdateLot({
    facilityId,
    inventoryTableFilterState,
    onUpdateSuccess: () => {
      setOpenModal(false);
      setSelectedLot(null);
    },
    pageSize,
    currentPage,
  });

  const tableColumns = getTableColumns({
    handleEditClick,
    formatMessage,
    roleName,
  });

  const {
    data: groupedLots,
    totalRows,
    loading: lotsLoading,
    error: lotsError,
  } = useSearchLots({
    inventoryTableFilterState,
    facilityId,
    currentPage,
    pageSize,
  });

  if (lotsError) return <ErrorState variant="inline" error={lotsError} />;

  return (
    <div data-testid="inventoryOverview">
      <Grid
        container
        spacing={2}
        className={classes.headerControls}
        data-testid="inventoryOverviewControls"
      >
        <Grid item xs={12} sm={6} md={3}>
          <LocationSelectWithAllOption
            value={locationId}
            onChange={setLocationId}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={nonPharmaFlag ? 5 : 7}>
          <SearchField
            fullWidth
            hideHelper
            placeholder={formatMessage({
              id: "inventory.overview.searchBar.placeholder",
              defaultMessage: "Search Inventory",
            })}
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
            onClear={() => setSearchTerm("")}
            data-testid="inventoryOverviewSearchBar"
          />
        </Grid>
        {nonPharmaFlag && (
          <Grid item xs={12} sm={6} md={2}>
            <ProductOriginFilter value={itemType} onChange={setItemType} />
          </Grid>
        )}
        <Grid item xs={12} sm={6} md={2}>
          <SortBySelect
            options={Object.values(sortByOptions)}
            value={sortBy}
            onChange={setSortBy}
          />
        </Grid>
      </Grid>
      <Table
        loading={lotsLoading || loading}
        columns={tableColumns}
        rows={groupedLots ?? []}
        noDataMessage={formatMessage({
          id: "inventory.overview.list.noData",
          defaultMessage: "No lots found for that search.",
        })}
        expandingGroups
        pagination={{
          page: currentPage,
          pageSize,
          totalRows,
          handlePageChange: (newPageNumber) => {
            setCurrentPage(newPageNumber);
          },
        }}
      />
      <EditLotModal
        isOpen={openModal}
        onClose={() => setOpenModal(false)}
        lot={selectedLot}
        handleLotEditSubmit={handleLotEditSubmit}
      />
    </div>
  );
};

export default InventoryOverview;
